import { useEffect, useState, useRef } from "react";
import api, { formatError } from "@/api/client";
import { resolveMediaUrl, uploadSpaceMedia } from "@/lib/media";
import { toast } from "sonner";
import { Upload, Copy, Trash2 } from "lucide-react";

export default function MediaTab({ space }) {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef();

  const load = () => api.get(`/spaces/${space.id}/media`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [space.id]);

  const upload = async (files) => {
    setUploading(true);
    let ok = 0;
    for (const file of files) {
      try {
        await uploadSpaceMedia(space.id, file);
        ok += 1;
      } catch (e) { toast.error(formatError(e)); }
    }
    setUploading(false);
    load();
    if (ok) toast.success(ok === 1 ? "Uploaded" : `Uploaded ${ok} files`);
  };

  const onDelete = async (id) => {
    if (!confirm("Delete this file?")) return;
    await api.delete(`/spaces/${space.id}/media/${id}`);
    load();
  };

  const copyUrl = (url) => {
    const fullUrl = resolveMediaUrl(url);
    navigator.clipboard.writeText(fullUrl);
    toast.success("URL copied");
  };

  const isImage = (type) => /image|png|jpg|jpeg|gif|webp|svg/i.test(type || "");

  return (
    <div className="p-6 max-w-7xl">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-2xl font-black tracking-tight">Media library</h2>
        <button onClick={() => inputRef.current?.click()} disabled={uploading}
          data-testid="upload-media-btn"
          className="bg-[#41B5E6] hover:bg-[#2A9BCC] text-white px-4 py-2 rounded-sm font-semibold text-sm flex items-center gap-2 disabled:opacity-50">
          <Upload className="w-4 h-4" />
          {uploading ? "Uploading…" : "Upload files"}
        </button>
        <input ref={inputRef} type="file" multiple className="hidden" onChange={(e) => upload(Array.from(e.target.files || []))} />
      </div>

      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); upload(Array.from(e.dataTransfer.files)); }}
        className="border-2 border-dashed border-zinc-300 rounded-sm p-6 text-center text-sm text-zinc-500 mb-6">
        Drag & drop files here to upload
      </div>

      {items.length === 0 ? (
        <div className="text-sm text-zinc-500 text-center p-12">No media files yet.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {items.map((m) => (
            <div key={m.id} className="bg-white border border-zinc-200 rounded-sm overflow-hidden group">
              <div className="aspect-square bg-zinc-50 flex items-center justify-center overflow-hidden">
                {isImage(m.file_type) ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${m.file_url}`} alt={m.original_name} className="w-full h-full object-cover" />
                ) : (
                  <div className="text-xs text-zinc-500 p-3 text-center">{m.original_name?.split('.').pop()?.toUpperCase()}</div>
                )}
              </div>
              <div className="p-2 text-xs">
                <div className="truncate font-semibold" title={m.original_name}>{m.original_name}</div>
                <div className="text-zinc-500 mt-0.5">{(m.file_size / 1024).toFixed(0)} KB</div>
                <div className="flex gap-1 mt-2">
                  <button onClick={() => copyUrl(m.file_url)} className="flex-1 px-2 py-1 text-[10px] border border-zinc-300 hover:bg-zinc-50 rounded-sm font-semibold uppercase tracking-wider flex items-center justify-center gap-1">
                    <Copy className="w-3 h-3" /> URL
                  </button>
                  <button onClick={() => onDelete(m.id)} className="px-2 py-1 text-[10px] border border-zinc-300 hover:bg-rose-50 hover:text-rose-600 rounded-sm">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
